import { useMemo, useState } from 'react';
import { can, type OpsBet } from '@urbanrats/ops-core';
import { fmtAtomic, fmtMoney, fmtTime, useCtx } from '../lib.ts';
import { ChangeRequests } from './ChangeRequests.tsx';
import { StateTag } from './common.tsx';

const VOIDABLE = ['BETTING_OPEN', 'BETTING_CLOSED', 'CAPTURING', 'VERIFYING', 'REVIEW_REQUIRED'];

/** Per-currency totals of cash stakes (never summed across currencies). */
function totals(bets: OpsBet[]): string {
  const m = new Map<string, { a: bigint; s: number }>();
  for (const b of bets) {
    const c = b.bet.cashDebit;
    const cur = m.get(c.currency) ?? { a: 0n, s: c.scale };
    m.set(c.currency, { a: cur.a + BigInt(c.amountAtomic), s: c.scale });
  }
  return [...m].map(([cur, v]) => fmtAtomic(v.a, v.s, cur)).join(' · ') || '—';
}

/**
 * Round refunds. Before the result is final: void the round → the wallet refunds every bet (rollback).
 * After settlement: goodwill refund of cash stakes (losing bets, or all) as journalled manual adjustments
 * the partner applies. Both need a second approver (maker ≠ checker).
 */
export function RoundRefunds({ roundId, state }: { roundId: string; state: string }) {
  const { data, user, backend, refresh } = useCtx();
  const [basis, setBasis] = useState<'LOSING_STAKES' | 'ALL_STAKES'>('LOSING_STAKES');
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const bets = data.bets.filter((b) => b.bet.gameRoundId === roundId);
  const settled = bets.filter((b) => b.bet.state === 'SETTLED' && BigInt(b.bet.cashDebit.amountAtomic) > 0n);
  const chosen = basis === 'ALL_STAKES' ? settled : settled.filter((b) => !b.bet.payoutGross || BigInt(b.bet.payoutGross.amountAtomic) === 0n);
  const voidable = VOIDABLE.includes(state);
  const canVoid = can(user, 'request.review');
  const canGoodwill = can(user, 'request.adjustment');
  const send = async (kind: 'ROUND_VOID' | 'GOODWILL_REFUND') => {
    const r =
      kind === 'ROUND_VOID'
        ? await backend.request(user, 'ROUND_VOID', roundId, `Void round ${roundId} and refund all ${bets.length} bets through the wallet`, { roundId }, reason)
        : await backend.request(user, 'GOODWILL_REFUND', roundId, `Goodwill refund of ${basis === 'ALL_STAKES' ? 'all' : 'losing'} cash stakes in ${roundId}: ${chosen.length} bets, ${totals(chosen)}`, { roundId, basis }, reason);
    setMsg(r.ok ? { ok: true, text: `Request ${r.value.requestId} created — a second person approves it below or in Audit.` } : { ok: false, text: `${r.error.code}: ${r.error.message}` });
    if (r.ok) setReason('');
    refresh();
  };
  return (
    <section className="section refunds" data-testid="round-refunds">
      <h2>Refunds</h2>
      {voidable ? (
        <>
          <p>
            The result is not final yet: <strong>voiding</strong> refunds every bet of this round through the wallet ({bets.length} bets, {totals(bets)} cash).
          </p>
          {canVoid ? (
            <div className="refund-form">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (audit), e.g. wrong line on this camera" aria-label="Reason" />
              <button type="button" className="btn danger" disabled={reason.trim().length < 10} onClick={() => void send('ROUND_VOID')} data-testid="request-round-void">
                Request void &amp; refund
              </button>
            </div>
          ) : (
            <p className="muted">Your role cannot request a void.</p>
          )}
        </>
      ) : state === 'SETTLED' ? (
        <>
          <p>
            Settled rounds are not rolled back (wallet contract: one outcome per bet). A <strong>goodwill refund</strong> credits the cash stake of chosen bets as a journalled manual adjustment; the partner applies it.
          </p>
          <div className="refund-form">
            <select value={basis} onChange={(e) => setBasis(e.target.value as 'LOSING_STAKES' | 'ALL_STAKES')} aria-label="Which bets">
              <option value="LOSING_STAKES">Losing bets ({settled.filter((b) => !b.bet.payoutGross || BigInt(b.bet.payoutGross.amountAtomic) === 0n).length})</option>
              <option value="ALL_STAKES">All settled bets ({settled.length})</option>
            </select>
            <span className="pill">{chosen.length} bets · {totals(chosen)}</span>
          </div>
          {canGoodwill ? (
            <div className="refund-form">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (audit), e.g. count disputed by players, camera glitch" aria-label="Reason" />
              <button type="button" className="btn danger" disabled={reason.trim().length < 10 || chosen.length === 0} onClick={() => void send('GOODWILL_REFUND')} data-testid="request-goodwill-round">
                Request goodwill refund
              </button>
            </div>
          ) : (
            <p className="muted">Your role cannot request refunds.</p>
          )}
        </>
      ) : (
        <p className="muted">{state === 'VOIDED' || state === 'VOIDING' ? 'This round was voided: every bet is refunded through the wallet.' : `No refund action while the round is ${state}.`}</p>
      )}
      {msg && <p className={msg.ok ? 'ok-box' : 'warn-box'}>{msg.text}</p>}
      <ChangeRequests filter={(q) => (q.kind === 'ROUND_VOID' || q.kind === 'GOODWILL_REFUND') && q.target === roundId} testId="round-refund-requests" />
    </section>
  );
}

/** A player's bets with goodwill refunds of chosen settled bets (maker-checker; partner applies). */
export function PlayerRefunds({ playerRef }: { playerRef: string }) {
  const { data, user, backend, refresh } = useCtx();
  const bets = useMemo(() => data.bets.filter((b) => b.playerRef === playerRef).sort((a, b) => (a.bet.submittedAt < b.bet.submittedAt ? 1 : -1)), [data, playerRef]);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refundable = (b: OpsBet) => b.bet.state === 'SETTLED' && BigInt(b.bet.cashDebit.amountAtomic) > 0n;
  const chosen = bets.filter((b) => pick.has(b.bet.betId));
  const canGoodwill = can(user, 'request.adjustment');
  const send = async () => {
    const r = await backend.request(user, 'GOODWILL_REFUND', playerRef, `Goodwill refund for player ${playerRef}: ${chosen.length} bets, ${totals(chosen)}`, { playerRef, betIds: chosen.map((b) => b.bet.betId).join(',') }, reason);
    setMsg(r.ok ? { ok: true, text: `Request ${r.value.requestId} created — a second person approves it.` } : { ok: false, text: `${r.error.code}: ${r.error.message}` });
    if (r.ok) {
      setReason('');
      setPick(new Set());
    }
    refresh();
  };
  return (
    <section className="section refunds" data-testid="player-refunds">
      <h2>Bets and refunds</h2>
      <p className="note">Pick settled bets to refund their cash stake as goodwill (journalled manual adjustment; the partner applies it). Unsettled bets are refunded by voiding their round.</p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th aria-label="Pick" />
              <th>Placed</th>
              <th>Round</th>
              <th>Selection</th>
              <th className="num">Stake</th>
              <th className="num">Return</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {bets.slice(0, 200).map((b) => (
              <tr key={b.key}>
                <td data-label="Pick">
                  <input
                    type="checkbox"
                    disabled={!refundable(b) || !canGoodwill}
                    checked={pick.has(b.bet.betId)}
                    aria-label={`pick bet ${b.bet.betId}`}
                    onChange={(e) => {
                      const n = new Set(pick);
                      if (e.target.checked) n.add(b.bet.betId);
                      else n.delete(b.bet.betId);
                      setPick(n);
                    }}
                  />
                </td>
                <td data-label="Placed">{fmtTime(b.bet.submittedAt)}</td>
                <td data-label="Round" className="mono">
                  {b.bet.gameRoundId}
                </td>
                <td data-label="Selection">{b.bet.selection}</td>
                <td data-label="Stake" className="num">
                  {fmtMoney(b.bet.stake)}
                </td>
                <td data-label="Return" className="num">
                  {b.bet.payoutGross ? fmtMoney(b.bet.payoutGross) : '—'}
                </td>
                <td data-label="State">
                  <StateTag state={b.bet.state} />
                </td>
              </tr>
            ))}
            {bets.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  No bets.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {canGoodwill && (
        <div className="refund-form">
          <span className="pill">
            {chosen.length} picked · {totals(chosen)}
          </span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (audit), e.g. complaint #123 accepted" aria-label="Reason" />
          <button type="button" className="btn danger" disabled={chosen.length === 0 || reason.trim().length < 10} onClick={() => void send()} data-testid="request-goodwill-player">
            Request goodwill refund
          </button>
        </div>
      )}
      {msg && <p className={msg.ok ? 'ok-box' : 'warn-box'}>{msg.text}</p>}
      <ChangeRequests filter={(q) => q.kind === 'GOODWILL_REFUND' && q.target === playerRef} testId="player-refund-requests" />
    </section>
  );
}
